/** @module Every scan of every user: find what the recognition gets wrong, exclude or delete it. */
import { Ban, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Switch } from "@/components/ui/controls";
import { Sheet } from "@/components/ui/overlays";
import {
  OUTCOMES,
  type Outcome,
  type ScanDetail,
  type ScanFilters,
  useAccounts,
  useAdminScans,
  useScanActions,
} from "@/features/admin/use-admin-scans";
import { dateTime } from "@/lib/format";
import { useAuthedImage } from "@/lib/images";

/** What happened to a scan, in the words of the filter. */
export function outcomeOf(scan: ScanDetail): Outcome {
  if (scan.status === "failed") return "failed";
  if (scan.status !== "done") return "pending";
  return scan.resolution?.resolution ?? "open";
}

const BADGE: Record<Outcome, string> = {
  open: "badge-warn",
  pending: "badge-neutral",
  failed: "badge-loss",
  auto: "badge-info",
  confirmed: "badge-gain",
  corrected: "badge-accent",
  dismissed: "badge-neutral",
  unapproved: "badge-warn",
};

/** Booked, but the label still waits for an administrator before it trains anything. */
function waitsForRelease(scan: ScanDetail): boolean {
  const resolution = scan.resolution;
  return Boolean(resolution?.final_print_id && !resolution.approved_at && !scan.training_excluded);
}

function picture(scan: ScanDetail, size: "thumb" | "large", whole = false): string | null {
  if (!whole && scan.has_crop) return `/api/v1/scans/${scan.id}/crop?size=${size}`;
  return scan.has_photo ? `/api/v1/scans/${scan.id}/photo?size=${size}` : null;
}

function ScanTile({ scan, onOpen }: { scan: ScanDetail; onOpen: () => void }) {
  const { t, i18n } = useTranslation();
  const url = useAuthedImage(picture(scan, "thumb"));
  const outcome = outcomeOf(scan);
  const card = scan.resolution?.final_print_id ?? scan.result?.chosen_print_id ?? null;
  return (
    <li>
      <button type="button" className="tile w-full" onClick={onOpen}>
        <span className="tile-media block">
          {url ? (
            <img src={url} alt="" className="card-shape w-full bg-(--surface-2) object-cover" />
          ) : (
            <span className="card-shape block w-full bg-(--surface-2)" />
          )}
        </span>
        <span className="row flex-wrap gap-1">
          <span className={`badge ${BADGE[outcome]}`}>
            {t(`admin.scanList.outcomes.${outcome}`)}
          </span>
          {scan.training_excluded ? (
            <span className="badge badge-loss">{t("admin.scanList.excludedShort")}</span>
          ) : null}
          {waitsForRelease(scan) ? (
            <span className="badge badge-warn">{t("admin.scanList.waitingShort")}</span>
          ) : null}
        </span>
        <span className="tile-name num truncate">{card ?? "–"}</span>
        <span className="t-xs t-muted truncate">
          {scan.owner?.display_name ?? "?"} ·{" "}
          {dateTime(scan.created_at, i18n.resolvedLanguage ?? "de")}
        </span>
      </button>
    </li>
  );
}

function Facts({ rows }: { rows: [string, string | null | undefined][] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[length:var(--text-sm)]">
      {rows
        .filter(([, value]) => value !== null && value !== undefined && value !== "")
        .map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="t-muted">{label}</dt>
            <dd className="num min-w-0 break-words">{value}</dd>
          </div>
        ))}
    </dl>
  );
}

function plain(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function Detail({ scan, onGone }: { scan: ScanDetail; onGone: () => void }) {
  const { t, i18n } = useTranslation();
  const crop = useAuthedImage(scan.has_crop ? picture(scan, "large") : null);
  const photo = useAuthedImage(picture(scan, "large", true));
  const { exclude, remove } = useScanActions(scan.id);
  const [confirming, setConfirming] = useState(false);
  const [excluded, setExcluded] = useState(scan.training_excluded);
  const result = scan.result;
  const candidates = (result?.candidates ?? []).slice(0, 5) as {
    print_id?: string;
    score?: number;
  }[];

  return (
    <div className="flex flex-col gap-4">
      <h2 className="t-h3">
        {scan.owner?.display_name ?? "?"} ·{" "}
        {dateTime(scan.created_at, i18n.resolvedLanguage ?? "de")}
      </h2>
      <div className="grid grid-cols-2 gap-3">
        {crop ? (
          <img
            src={crop}
            alt={t("admin.scanList.crop")}
            className="w-full rounded-[var(--radius-sm)]"
          />
        ) : null}
        {photo ? (
          <img
            src={photo}
            alt={t("admin.scanList.photo")}
            className="w-full rounded-[var(--radius-sm)]"
          />
        ) : null}
      </div>
      <Facts
        rows={[
          [t("admin.scanList.outcome"), t(`admin.scanList.outcomes.${outcomeOf(scan)}`)],
          [t("admin.scanList.final"), scan.resolution?.final_print_id],
          [
            t("admin.scanList.decision"),
            result ? `${result.decision} (${result.reasons.join(", ")})` : null,
          ],
          [t("admin.scanList.ocrConfirms"), result ? (result.ocr_confirms ? "✓" : "✗") : null],
          [t("admin.scanList.ocr"), result ? plain(result.ocr) : null],
          [
            t("admin.scanList.detection"),
            result ? `${result.detection_path} · ${result.detection_score.toFixed(2)}` : null,
          ],
          [t("admin.scanList.error"), scan.error],
          [t("admin.scanList.benchmark"), scan.benchmark_opt_in ? "✓" : "✗"],
        ]}
      />
      {candidates.length > 0 ? (
        <section className="flex flex-col gap-1">
          <h3 className="t-label">{t("admin.scanList.candidates")}</h3>
          <ol className="t-sm num">
            {candidates.map((candidate, index) => (
              <li key={`${candidate.print_id ?? index}`}>
                {index + 1}. {candidate.print_id ?? "?"}
                {typeof candidate.score === "number" ? ` · ${candidate.score.toFixed(3)}` : ""}
              </li>
            ))}
          </ol>
        </section>
      ) : null}
      <section className="flex flex-col gap-1">
        <h3 className="t-label">{t("admin.scanList.capture")}</h3>
        <Facts
          rows={Object.entries(scan.capture ?? {}).map(([key, value]) => [key, plain(value)])}
        />
      </section>
      <div className="row-between min-h-11">
        <span className="flex flex-col">
          <span>{t("admin.scanList.exclude")}</span>
          <span className="t-xs t-muted">{t("admin.scanList.excludeHint")}</span>
        </span>
        <Switch
          checked={excluded}
          label={t("admin.scanList.exclude")}
          onChange={(next) => {
            setExcluded(next);
            exclude.mutate(next, {
              onError: () => {
                setExcluded(!next);
              },
            });
          }}
        />
      </div>
      {confirming ? (
        <div className="callout callout-warn flex-col items-stretch gap-2">
          <span>{t("admin.scanList.deleteConfirm")}</span>
          <div className="row gap-2">
            <button
              type="button"
              className="btn btn-danger grow"
              disabled={remove.isPending}
              onClick={() => {
                remove.mutate(undefined, { onSuccess: onGone });
              }}
            >
              <Trash2 />
              {t("admin.scanList.deleteNow")}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-ghost self-start"
          onClick={() => {
            setConfirming(true);
          }}
        >
          <Trash2 />
          {t("admin.scanList.delete")}
        </button>
      )}
      {remove.isError || exclude.isError ? (
        <p role="alert" className="t-sm loss">
          {t("common.loadError")}
        </p>
      ) : null}
    </div>
  );
}

export function ScanList() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<ScanFilters>({
    userId: null,
    outcome: null,
    excluded: null,
  });
  const [open, setOpen] = useState<ScanDetail | null>(null);
  const accounts = useAccounts();
  const page = useAdminScans(filters);
  const scans = page.data?.pages.flatMap((one) => one.items) ?? [];

  return (
    <section className="flex flex-col gap-4">
      <h2 className="t-h3">{t("admin.scanList.title")}</h2>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[length:var(--text-sm)]">
          <span className="t-muted">{t("admin.scanList.person")}</span>
          <select
            className="input"
            value={filters.userId ?? ""}
            onChange={(event) => {
              setFilters({ ...filters, userId: event.target.value || null });
            }}
          >
            <option value="">{t("admin.scanList.everybody")}</option>
            {(accounts.data ?? []).map((user) => (
              <option key={user.id} value={user.id}>
                {user.display_name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[length:var(--text-sm)]">
          <span className="t-muted">{t("admin.scanList.outcome")}</span>
          <select
            className="input"
            value={filters.outcome ?? ""}
            onChange={(event) => {
              setFilters({ ...filters, outcome: (event.target.value || null) as Outcome | null });
            }}
          >
            <option value="">{t("admin.scanList.anyOutcome")}</option>
            {OUTCOMES.map((outcome) => (
              <option key={outcome} value={outcome}>
                {t(`admin.scanList.outcomes.${outcome}`)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="chip"
          aria-pressed={filters.excluded === true}
          onClick={() => {
            setFilters({ ...filters, excluded: filters.excluded === true ? null : true });
          }}
        >
          <Ban />
          {t("admin.scanList.onlyExcluded")}
        </button>
      </div>

      {page.isPending ? <p className="t-muted">{t("common.loading")}</p> : null}
      {page.isError ? (
        <p role="alert" className="loss">
          {t("common.loadError")}
        </p>
      ) : null}
      {page.isSuccess && scans.length === 0 ? (
        <p className="t-muted">{t("admin.scanList.empty")}</p>
      ) : null}
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
        {scans.map((scan) => (
          <ScanTile
            key={scan.id}
            scan={scan}
            onOpen={() => {
              setOpen(scan);
            }}
          />
        ))}
      </ul>
      {page.hasNextPage ? (
        <button
          type="button"
          className="btn btn-secondary self-center"
          disabled={page.isFetchingNextPage}
          onClick={() => {
            void page.fetchNextPage();
          }}
        >
          {t("admin.scanList.more")}
        </button>
      ) : null}

      <Sheet
        open={open !== null}
        onClose={() => {
          setOpen(null);
        }}
        label={t("admin.scanList.detail")}
      >
        {open ? (
          <Detail
            key={open.id}
            scan={open}
            onGone={() => {
              setOpen(null);
            }}
          />
        ) : null}
      </Sheet>
    </section>
  );
}
